import { LocalTime } from '@js-joda/core';

import { commonTranslationKeys, FormContextValues, useTranslate } from '@dreryk/react-common';
import { FormLabel, FormValue, TimePicker } from '@dreryk/react-components';

import { personnelTranslationKeys } from '../../../../translations';
import { StyledTimeRow } from '../../StyledTimeRow';
import { TimePickerRangeType } from '../../common.types';
import { castToLocalTime, MAX_TIME, MIN_TIME, TIME_STEP_IN_MINUTES } from '../../common.utils';

import { StyledAvailabilityTimeRange } from './StyledAvailabilityTimeRange';

type AvailabilityTimeRangeFormBean = {
   startTime: LocalTime;
   endTime: LocalTime;
};
type AvailabilityTimeRangeFormController = FormContextValues<AvailabilityTimeRangeFormBean>;
type AvailabilityTimeRangeProps = {
   readonly register: AvailabilityTimeRangeFormController['register'];
   readonly adjustStartTimeOrEndTime: (rangeType: TimePickerRangeType, value: LocalTime, stepInMinutes: number) => void;
};

export const AvailabilityTimeRange = (props: AvailabilityTimeRangeProps): JSX.Element => {
   const translate = useTranslate();
   const { register } = props;
   return (
      <StyledAvailabilityTimeRange>
         <FormLabel label={commonTranslationKeys.hour} required testId={'hour'} />
         <StyledTimeRow>
            <TimePicker
               testId={'startTime'}
               controller={register('startTime', {
                  required: true,
                  validate: (data) => {
                     /*
                      * Castowanie wartości 'data' do LocalTime jest konieczne, ponieważ są one zwracane z typem 'unknown'.
                      * useFormController nie przypisuje prawidłowo typów do wartości formularza.
                      * Prawdopodobna przyczyna opisana w https://dreryk.atlassian.net/browse/DRWEB-3446
                      * Castowanie to jest bezpieczne, ponieważ komponent TimePicker, z którego ta wartość pochodzi zwraca
                      * wartość właśnie o typie LocalTime.
                      */
                     props.adjustStartTimeOrEndTime(
                        TimePickerRangeType.END,
                        castToLocalTime(data),
                        TIME_STEP_IN_MINUTES
                     );
                     return undefined;
                  },
               })}
               min={MIN_TIME}
               max={MAX_TIME.minusMinutes(TIME_STEP_IN_MINUTES)}
               stepInMinutes={TIME_STEP_IN_MINUTES}
               withoutRequiredMark
            />
            <FormValue value={translate(personnelTranslationKeys.toHour)} testId={'toHour'} />
            <TimePicker
               testId={'endTime'}
               controller={register('endTime', {
                  required: true,
                  validate: (data) => {
                     /*
                      * Castowanie wartości 'data' do LocalTime jest konieczne, ponieważ są one zwracane z typem 'unknown'.
                      * useFormController nie przypisuje prawidłowo typów do wartości formularza.
                      * Prawdopodobna przyczyna opisana w https://dreryk.atlassian.net/browse/DRWEB-3446
                      * Castowanie to jest bezpieczne, ponieważ komponent TimePicker, z którego ta wartość pochodzi zwraca
                      * wartość właśnie o typie LocalTime.
                      */
                     props.adjustStartTimeOrEndTime(
                        TimePickerRangeType.START,
                        castToLocalTime(data),
                        TIME_STEP_IN_MINUTES
                     );
                     return undefined;
                  },
               })}
               min={MIN_TIME.plusMinutes(TIME_STEP_IN_MINUTES)}
               max={MAX_TIME}
               stepInMinutes={TIME_STEP_IN_MINUTES}
               withoutRequiredMark
            />
         </StyledTimeRow>
      </StyledAvailabilityTimeRange>
   );
};
